/**
 * `Menu` (W1-12 ui-kit-i18n; contract MenuProps): a dropdown menu (Radix) opened from `trigger` (click, Enter,
 * Space or ArrowDown). Arrow keys move through items (looping), typeahead jumps to a label, Escape closes and
 * returns focus to the trigger. Command items run through the UiBridge and show their shortcut and disabled
 * reason (see internal/menuItems.tsx). The trigger element must accept a ref and DOM props (Button and
 * IconButton do); other content is wrapped in a button.
 */

import * as DM from '@radix-ui/react-dropdown-menu';
import { isValidElement, type ReactNode } from 'react';
import { asTriggerChild } from './internal/commandButton';
import { keepFocusOnPointerDown } from './internal/focus';
import { MenuItems, menuStyles, type MenuKit } from './internal/menuItems';
import type { MenuProps } from './types';

const KIT: MenuKit = {
  Item: DM.Item,
  CheckboxItem: DM.CheckboxItem,
  ItemIndicator: DM.ItemIndicator,
  Separator: DM.Separator,
  Sub: DM.Sub,
  SubTrigger: DM.SubTrigger,
  SubContent: DM.SubContent,
  Portal: DM.Portal,
};

export interface MenuExtraProps {
  readonly align?: 'start' | 'center' | 'end';
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  /** Accessible name of the menu (defaults to the trigger's name through aria-labelledby). */
  readonly label?: string;
}

function triggerElement(trigger: ReactNode): ReactNode {
  if (isValidElement(trigger)) return asTriggerChild(trigger);
  return (
    <button type="button" className={menuStyles.textTrigger} onMouseDown={keepFocusOnPointerDown}>
      {trigger}
    </button>
  );
}

export function Menu(props: MenuProps & MenuExtraProps) {
  return (
    <DM.Root>
      <DM.Trigger asChild>{triggerElement(props.trigger)}</DM.Trigger>
      <DM.Portal>
        <DM.Content
          className={menuStyles.content}
          align={props.align ?? 'start'}
          side={props.side ?? 'bottom'}
          sideOffset={4}
          collisionPadding={8}
          loop
          aria-label={props.label}
        >
          <MenuItems kit={KIT} items={props.items} />
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}
